import { forwardRef, useEffect, useImperativeHandle, useState, useCallback } from 'react'

export interface WikiLinkItem {
  id: number
  title: string
  slug: string
  title_path: string
}

interface WikiLinkListProps {
  items: WikiLinkItem[]
  command: (item: WikiLinkItem) => void
}

export interface WikiLinkListRef {
  onKeyDown: (props: { event: KeyboardEvent }) => boolean
}

export const WikiLinkList = forwardRef<WikiLinkListRef, WikiLinkListProps>(
  ({ items, command }, ref) => {
    const [selectedIndex, setSelectedIndex] = useState(0)

    useEffect(() => {
      setSelectedIndex(0)
    }, [items])

    const selectItem = useCallback(
      (index: number) => {
        const item = items[index]
        if (item) {
          command(item)
        }
      },
      [items, command],
    )

    useImperativeHandle(ref, () => ({
      onKeyDown: ({ event }: { event: KeyboardEvent }) => {
        if (event.key === 'ArrowUp') {
          setSelectedIndex((prev) => (prev + items.length - 1) % items.length)
          return true
        }
        if (event.key === 'ArrowDown') {
          setSelectedIndex((prev) => (prev + 1) % items.length)
          return true
        }
        if (event.key === 'Enter') {
          selectItem(selectedIndex)
          return true
        }
        return false
      },
    }))

    if (items.length === 0) {
      return (
        <div className="overflow-hidden rounded-lg border border-gray-200 bg-white p-2 text-sm text-gray-500 shadow-lg">
          No pages found
        </div>
      )
    }

    return (
      <div className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-lg">
        {items.map((item, index) => (
          <button
            key={item.id}
            type="button"
            className={`block w-full px-3 py-2 text-left text-sm ${
              index === selectedIndex
                ? 'bg-indigo-50 text-indigo-700'
                : 'text-gray-700 hover:bg-gray-50'
            }`}
            onClick={() => selectItem(index)}
          >
            <span>{item.title}</span>
            {item.title_path.includes('/') && (
              <span className="ml-2 text-xs text-gray-400">
                {item.title_path.slice(0, item.title_path.lastIndexOf('/'))}
              </span>
            )}
          </button>
        ))}
      </div>
    )
  },
)

WikiLinkList.displayName = 'WikiLinkList'
